// @flow
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getJson, postJson, del } from 'utils/http';
import type { ListingApplicationPayload } from 'api/ats';
import type { CmsSubscribe } from 'api/cms';
import { removeParam } from 'utils/url';
import { saveSearch } from '../redux/main/actions/currentUser';

const { useCallback, useEffect, useReducer, useState } = React;

type TmpListingApplicationPayload = {
  ...ListingApplicationPayload
};

type SavedSearchUnsaved = {|
  text: string,
  filters: Object,
  location: SearchLocation,
  radius: ?number,
  index: 'MAIN' | 'ACTIONS'
|};

type ActionType =
  | 'LISTING_APPLICATION_VALUES'
  | 'SAVE_SEARCH'
  | 'CMS_SUBSCRIBE';

type ListingApplicationValuesAction = {
  type: 'LISTING_APPLICATION_VALUES',
  data: TmpListingApplicationPayload
};

type SaveSearchAction = {
  type: 'SAVE_SEARCH',
  data: SavedSearchUnsaved
};

type CmsSubscribeAction = {
  type: 'CMS_SUBSCRIBE',
  data: CmsSubscribe
};

type Action =
  | ListingApplicationValuesAction
  | SaveSearchAction
  | CmsSubscribeAction;

export default function useTemporaryStorage<T: {}>(
  tmpId: ?string
): [({ [key: ActionType]: T }) => Promise<string>, { [key: string]: T }] {
  const [loaded, setLoaded] = useState(false);
  const reduxDispatch = useDispatch();
  const { isLoggedIn, hasRequiredInfo } = useSelector(state => ({
    isLoggedIn:
      state.currentUser &&
      state.currentUser.id &&
      state.currentUser.emailVerified,
    hasRequiredInfo: !!(
      state.currentUser.firstName && state.currentUser.lastName
    )
  }));

  const mapStateToTemporaryStorage = (state: {
    [key: ActionType]: T
  }): Array<{ type: ActionType, data: T }> =>
    Object.keys(state).map((key: ActionType) => ({
      type: key,
      data: state[key]
    }));

  const saveTemporaryStorage = (state: { [key: ActionType]: T }) =>
    postJson('/data/tmp-storage', {
      data: JSON.stringify(mapStateToTemporaryStorage(state))
    }).then(({ data: { id } }) => id);

  const memoizedReducer = useCallback(
    (state: { [key: ActionType]: T }, action: Action): T => {
      switch (action.type) {
        case 'LISTING_APPLICATION_VALUES': {
          return {
            ...state,
            initialValues: {
              id: action.data.id,
              firstName: action.data.firstName,
              lastName: action.data.lastName,
              email: action.data.email,
              requestedAttachments: action.data.requestedAttachments,
              resumeS3FileId: action.data.resumeS3FileId,
              type: action.data.type,
              consentAts: action.data.consentAts
            }
          };
        }
        case 'SAVE_SEARCH':
          return reduxDispatch(saveSearch(action.data));
        case 'CMS_SUBSCRIBE': {
          return {
            ...state,
            blockId: action.data.blockId,
            index: action.data.index
          };
        }
        default:
          (action.type: empty);
          throw new Error(`Undefined temporary storage key ${action.type}`);
      }
    },
    [reduxDispatch]
  );

  const [temporaryStorage, dispatch] = useReducer(
    memoizedReducer,
    Object.freeze({})
  );

  useEffect(
    () => {
      const mapTemporaryStorageToState = (serializedTemporaryStorage: string) =>
        JSON.parse(serializedTemporaryStorage).forEach((action: Action) =>
          dispatch(action)
        );

      const deleteTemporaryStorage = (temporaryStorageId: string) =>
        del(`/data/tmp-storage/${temporaryStorageId}`);

      const getTemporaryStorage = (temporaryStorageId: string) =>
        getJson(`/data/tmp-storage/${temporaryStorageId}`).then(({ data }) => {
          setLoaded(true);
          deleteTemporaryStorage(temporaryStorageId);
          const newUrl = `${window.location.pathname}?${removeParam(
            window.location.search.replace('?', ''),
            'tmpId'
          )}`;
          window.history.pushState({ path: newUrl }, '', newUrl);

          return mapTemporaryStorageToState(data);
        });

      if (tmpId && !loaded && isLoggedIn && hasRequiredInfo) {
        getTemporaryStorage(tmpId);
      }
    },
    [hasRequiredInfo, isLoggedIn, loaded, setLoaded, temporaryStorage, tmpId]
  );

  return [saveTemporaryStorage, temporaryStorage];
}
